import { useEffect, useState } from "react";
import { Zap, Plus, Trash2, Pencil, Play, Loader2, GitBranch, Maximize2, AlertTriangle, UserRound, Settings2, Star, Search, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { TRIGGERS, TRIGGER_GROUPS, validateSteps, findCycle, cycleMessage } from "./automationMeta";
import { FlowEditor } from "./AutomationEditor";
import { CopyToChannelButton } from "@/components/CopyToChannelButton";
import { DiagnoseDialog, DropAlertsDialog } from "@/components/AutomationTools";
import { PendingDelaysPanel } from "@/components/PendingDelaysPanel";
export { findCycle };

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Automazioni() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("all");
  const [onlyFav, setOnlyFav] = useState(false);

  const folders = [...new Set(items.map((a) => a.folder).filter(Boolean))].sort();
  const visible = items.filter((a) => (!q.trim() || (a.name || "").toLowerCase().includes(q.trim().toLowerCase()))
    && (folder === "all" || (folder === "__none__" ? !a.folder : a.folder === folder)) && (!onlyFav || a.favorite));

  const duplicate = async (a) => {
    try {
      const { data } = await api.post(`/automations/${a.id}/duplicate`);
      toast.success(`Creata «${data.name}» (disattivata): modificala e attivala quando è pronta`);
      await load();
      setEditing(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Duplicazione non riuscita"); }
  };

  const load = async () => {
    const { data } = await api.get("/automations");
    setItems(data);
    setLoading(false);
    return data;
  };
  useEffect(() => {
    load().then((data) => {
      const openId = new URLSearchParams(window.location.search).get("open");
      const a = openId && (data || []).find((x) => x.id === openId);
      if (a) setEditing(a);
    });
  }, []);

  const toggle = async (a) => {
    await api.put(`/automations/${a.id}`, { ...a, enabled: !a.enabled });
    load();
  };
  const remove = async (id) => {
    await api.delete(`/automations/${id}`);
    toast.success("Automazione eliminata");
    load();
  };
  const createDraft = async () => {
    try {
      const n = items.length + 1;
      const { data } = await api.post("/automations", { name: `Automazione ${n}`, enabled: false, trigger_type: "keyword", trigger_value: "", priority: 0, start_key: "", steps: [] });
      await load();
      setEditing(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Creazione non riuscita"); }
  };
  const openEditorAfterCreate = async (created) => {
    await load();
    if (created?.id) setEditing(created);
  };

  return (
    <div className="animate-fade-up -mx-4 sm:-mx-8 -my-6 min-h-[calc(100vh-4rem)] bg-[#F7F7F8] text-slate-800 px-4 sm:px-8 py-6 space-y-6" data-testid="automations-page" style={{ fontFamily: "'Manrope', 'Inter', system-ui, sans-serif" }}>
      {editing && (
        <FlowEditor automation={editing} onClose={() => { setEditing(null); load(); }} onSaved={() => load()}
                    renderStepPanel={(step, steps, patch, refs) => (
                      <div className="space-y-2">
                        <StepActionEditor st={step} refs={refs} onChange={(action) => patch({ action })} />
                        {step.kind !== "condition" && (
                          <div className="bg-[#111827] border border-[#1F2937] rounded-lg p-2 flex items-center gap-2" data-testid={`step-fallback-${step.key}`} title="Il testo libero è consentito da Meta solo entro 24 ore dall'ultimo messaggio del cliente. Fuori finestra viene inviato SOLO il template scelto qui; senza template l'invio resta bloccato.">
                            <span className="text-[11px] text-amber-400 shrink-0">Se finestra 24h chiusa →</span>
                            <select value={step.fallback_template_id || ""} data-testid={`step-fallback-select-${step.key}`} onChange={(e) => patch({ fallback_template_id: e.target.value || null })}
                                    className="flex-1 min-w-0 h-8 rounded border border-[#1F2937] bg-[#0A0E17] text-xs text-slate-100 px-2">
                              <option value="">nessun template (invio bloccato)</option>
                              {(refs.templates || []).filter((t) => t.status === "approved" && (t.meta_name || t.meta_template_name)).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          </div>
                        )}
                        <StepCaptureEditor st={step} steps={steps} onChange={(capture) => patch({ capture })} />
                        <StepUpdatesEditor st={step} onChange={(updates) => patch({ updates })} />
                        {(step.options || []).length > 0 && <StepListUiEditor st={step} onChange={patch} />}
                      </div>
                    )}
                    renderSettings={(auto, onDone) => (
                      <AutomationDialog existing={auto} onDone={(updated) => onDone(updated)} trigger={
                        <button data-testid="editor-settings-button" title="Impostazioni: trigger, azioni e campi" className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-700"><Settings2 className="h-5 w-5" /></button>
                      } />
                    )} />
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Automazioni</h1>
          <p className="text-slate-500 text-sm mt-1">Risposte automatiche con trigger e percorsi a step · clicca un'automazione per aprire il costruttore visuale</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <SimulateDialog />
          <DiagnoseDialog />
          <DropAlertsDialog />
          <TemplatesDialog onInstalled={async (a) => { await load(); setEditing(a); }} />
          <Button onClick={createDraft} data-testid="create-automation-button" className="bg-[#5BC65F] hover:bg-[#45B049] text-white font-semibold rounded-lg">
            <Plus className="h-4 w-4 mr-2" /> Nuova automazione
          </Button>
          <AutomationDialog onDone={openEditorAfterCreate} trigger={
            <Button variant="outline" data-testid="create-automation-advanced-button" title="Crea compilando il modulo completo" className="bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg">
              <Pencil className="h-4 w-4 mr-2" /> Modulo
            </Button>
          } />
        </div>
      </div>

      <PendingDelaysPanel />

      {loading ? (
        <p className="text-slate-500 text-center py-10">Caricamento...</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm">
          <Zap className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessuna automazione. Crea la prima risposta automatica.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="automations-list">
          <div className="flex flex-wrap items-center gap-2" data-testid="automations-filters">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome…" data-testid="automations-search" className="bg-white border-slate-200 text-slate-900 pl-8 h-9 w-56 focus-visible:ring-[#5BC65F]" />
            </div>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger className="bg-white border-slate-200 text-slate-800 h-9 w-48" data-testid="automations-folder-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-800">
                <SelectItem value="all">Tutte le cartelle</SelectItem>
                <SelectItem value="__none__">Senza cartella</SelectItem>
                {folders.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <button type="button" onClick={() => setOnlyFav((v) => !v)} data-testid="automations-fav-filter"
                    className={`h-9 px-3 rounded-lg border text-sm flex items-center gap-1.5 transition-colors ${onlyFav ? "bg-[#FEF3C7] border-[#F59E0B]/50 text-[#B45309]" : "bg-white border-slate-200 text-slate-500 hover:text-slate-800"}`}>
              <Star className={`h-4 w-4 ${onlyFav ? "fill-[#F59E0B] text-[#F59E0B]" : ""}`} /> Preferite
            </button>
            <span className="text-xs text-slate-500" data-testid="automations-count">{visible.length} di {items.length}</span>
          </div>
          {visible.length === 0 && <p className="text-sm text-slate-500 py-6 text-center" data-testid="automations-empty-filter">Nessuna automazione corrisponde ai filtri.</p>}
          {visible.map((a) => {
            const tr = TRIGGERS[a.trigger_type] || TRIGGERS.keyword;
            return (
              <div key={a.id} data-testid={`automation-row-${a.id}`}
                   className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-4 shadow-sm hover:shadow-md transition-shadow">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${a.enabled ? "bg-[#5BC65F]/15" : "bg-slate-100"}`}>
                  <Zap className={`h-5 w-5 ${a.enabled ? "text-[#45B049]" : "text-slate-400"}`} />
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEditing(a)} data-testid={`automation-open-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {a.favorite && <Star className="h-4 w-4 text-[#F59E0B] fill-[#F59E0B]" />}
                    <p className="font-semibold text-slate-900 hover:text-[#45B049]">{a.name}</p>
                    {a.folder && <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">{a.folder}</Badge>}
                    <Badge className={`${tr.cls} border-0 text-xs max-w-[320px] truncate`}>{tr.label}{a.trigger_value ? `: ${a.trigger_value}` : ""}</Badge>
                    <Badge className="bg-slate-100 text-slate-600 border-0 text-xs"><GitBranch className="h-3 w-3 mr-1" />{(a.steps || []).length} step</Badge>
                    <Badge className="bg-[#5BC65F]/15 text-[#2F7D32] border-0 text-xs" data-testid={`automation-fires-${a.id}`}>
                      <Play className="h-3 w-3 mr-1" />{a.fires || 0} attivazioni
                    </Badge>
                  </div>
                  {a.last_fired_at && <p className="text-[11px] text-slate-500 mt-0.5">Ultima attivazione: {new Date(a.last_fired_at).toLocaleString("it-IT")}{a.fired_recipients ? ` · ${a.fired_recipients} invii` : ""}</p>}
                  {a.steps?.[0] && <p className="text-sm text-slate-500 mt-1 line-clamp-1">{a.steps[0].message}</p>}
                  {a.trigger_type === "webhook" && (
                    <p className="text-xs text-[#EC4899] font-mono mt-1 break-all" data-testid={`automation-webhook-url-${a.id}`}>
                      POST {BACKEND}/api/public/automations/{a.id}/trigger
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => setEditing(a)} data-testid={`flow-open-${a.id}`} className="text-slate-400 hover:text-[#45B049]" title="Costruttore visuale"><Maximize2 className="h-4 w-4" /></button>
                  <button onClick={() => duplicate(a)} data-testid={`duplicate-automation-${a.id}`} className="text-slate-400 hover:text-[#8B5CF6]" title="Duplica"><Copy className="h-4 w-4" /></button>
                  <CopyToChannelButton endpoint={`/automations/${a.id}/copy-to`} itemName={a.name} testId={`copy-automation-${a.id}`} note="La copia arriva disattivata: aprila nel canale di destinazione e attivala quando è pronta." />
                  <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`automation-toggle-${a.id}`} className="data-[state=checked]:bg-[#5BC65F] data-[state=unchecked]:bg-slate-300" />
                  <AutomationDialog existing={a} onDone={load} trigger={
                    <button data-testid={`edit-automation-${a.id}`} className="text-slate-400 hover:text-[#3B82F6]"><Pencil className="h-4 w-4" /></button>
                  } />
                  <button onClick={() => remove(a.id)} data-testid={`delete-automation-${a.id}`} className="text-slate-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function CycleWarning({ steps }) {
  const cyc = findCycle(steps || []);
  if (!cyc) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#F59E0B]" data-testid="automation-cycle-warning">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <div>
        <p className="font-medium">{cycleMessage(steps, cyc)}</p>
        <p className="text-[#F59E0B]/80 mt-0.5">Il cliente potrebbe tornare allo stesso menu all'infinito. Va bene se è voluto (es. "Torna al menu"), altrimenti correggi i collegamenti.</p>
      </div>
    </div>
  );
}

function StepListUiEditor({ st, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2" data-testid={`step-list-ui-${st.key}`}>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input type="checkbox" checked={st.ui === "list"} onChange={(e) => onChange({ ui: e.target.checked ? "list" : undefined })} data-testid={`step-list-toggle-${st.key}`} />
        Menu lista nativo WhatsApp
      </label>
      {st.ui === "list" && (
        <Input value={st.list_button || ""} onChange={(e) => onChange({ list_button: e.target.value.slice(0, 20) })} placeholder="Testo pulsante (es. Scegli)"
               className="h-7 text-xs bg-[#111827] border-[#1F2937] w-44" data-testid={`step-list-button-${st.key}`} />
      )}
      <span className="text-[10px] text-slate-500">{st.ui === "list" ? "Il cliente tocca una voce dal menu (max 10, titolo ≤ 24 caratteri)." : "Opzioni numerate nel testo (1., 2., …)."}</span>
    </div>
  );
}

const CONTACT_FIELDS = [["nome", "Nome"], ["email", "Email"], ["phone", "Telefono"], ["store", "Punto vendita"], ["city", "Città"], ["card_code", "Codice carta"]];

function templateVars(tpl) {
  if (!tpl) return [];
  if (tpl.variables?.length) return tpl.variables.map((v) => `{{${v}}}`);
  const nums = [...new Set([...(tpl.body || "").matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => m[1]))];
  return nums.map((n) => `{{${n}}}`);
}

function TemplateParamsEditor({ tpl, action, onChange, customFields = [], stepKey }) {
  const vars = templateVars(tpl);
  const fields = [...CONTACT_FIELDS, ...customFields.filter((k) => !CONTACT_FIELDS.some(([v]) => v === k)).map((k) => [k, `${k} · personalizzato`])];
  const defaultFor = (v, i) => {
    const n = v.replace(/[{}]/g, "").trim();
    if (fields.some(([f]) => f === n)) return `{{${n}}}`;
    return i === 0 ? "{{nome}}" : "";
  };
  const params = vars.map((v, i) => (action.params?.[i] ?? defaultFor(v, i)));
  useEffect(() => {
    if (vars.length && !action.params) onChange({ ...action, params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl?.id]);
  if (!tpl) return null;
  if (!vars.length) return <p className="text-[11px] text-slate-500" data-testid={`step-template-params-${stepKey}`}>Il template non ha variabili.</p>;
  const setParam = (i, val) => { const p = [...params]; p[i] = val; onChange({ ...action, params: p }); };
  return (
    <div className="space-y-1.5 bg-[#0A0E17] rounded-md p-2" data-testid={`step-template-params-${stepKey}`}>
      <p className="text-[11px] text-slate-400">Variabili del template → campo anagrafica del cliente</p>
      {vars.map((v, i) => {
        const m = (params[i] || "").match(/^\{\{\s*([^}|]+?)\s*\}\}$/);
        const sel = m && fields.some(([f]) => f === m[1]) ? m[1] : "__text";
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-[#8B5CF6] w-20 shrink-0 truncate" title={v}>{v}</span>
            <Select value={sel} onValueChange={(val) => setParam(i, val === "__text" ? "" : `{{${val}}}`)}>
              <SelectTrigger className="h-7 text-xs flex-1 bg-[#111827] border-[#1F2937]" data-testid={`step-template-param-${stepKey}-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
                {fields.map(([f, l]) => <SelectItem key={f} value={f}>{l}</SelectItem>)}
                <SelectItem value="__text">Testo fisso…</SelectItem>
              </SelectContent>
            </Select>
            {sel === "__text" && (
              <Input value={params[i]} onChange={(e) => setParam(i, e.target.value)} placeholder="Valore fisso" data-testid={`step-template-param-text-${stepKey}-${i}`}
                     className="h-7 text-xs flex-1 bg-[#111827] border-[#1F2937]" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepActionEditor({ st, refs, onChange }) {
  const action = st.action || { type: "none", ref: "" };
  const type = action.type || "none";
  const setType = (t) => onChange(t === "none" ? null : { type: t, ref: "" });
  const setRef = (r) => onChange({ type, ref: r });
  const items = type === "template" ? refs.templates : type === "webhook" ? refs.webhooks : type === "automation" ? refs.automations : type === "cluster" ? refs.clusters : type === "email" ? (refs.emailTemplates || []) : [];
  const labelOf = (it) =>
    type === "template" ? `${it.name}${it.status ? ` · ${it.status}` : ""}`
      : type === "webhook" ? it.url
        : type === "cluster" ? `${it.name}${typeof it.contact_count === "number" ? ` · ${it.contact_count} contatti` : ""}`
          : it.name;

  return (
    <div className="bg-[#111827] border border-[#1F2937] rounded-lg p-2 space-y-2" data-testid={`step-action-${st.key}`}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-slate-500 shrink-0">Azione</span>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-8 flex-1" data-testid={`step-action-type-${st.key}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
            <SelectItem value="none">Nessuna (solo messaggio)</SelectItem>
            <SelectItem value="template">Invia un template</SelectItem>
            <SelectItem value="webhook">Chiama un webhook</SelectItem>
            <SelectItem value="automation">Avvia un'altra automazione</SelectItem>
            <SelectItem value="cluster">Iscrivi a un cluster</SelectItem>
            <SelectItem value="sms">Invia un SMS (SMSFactor)</SelectItem>
            <SelectItem value="email">Invia un'email transazionale</SelectItem>
            <SelectItem value="operator">Passa all'operatore (IA in pausa)</SelectItem>
            <SelectItem value="link">Invia un link (fisso o variabile)</SelectItem>
            <SelectItem value="pause">Metti in pausa il contatto (alternativa allo STOP)</SelectItem>
            <SelectItem value="form">Invia un FORM (WhatsApp Flow pubblicato)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {type === "form" && (
        <div className="space-y-1" data-testid={`step-action-form-${st.key}`}>
          <select value={action.ref || ""} onChange={(e) => onChange({ ...action, type, ref: e.target.value })} data-testid={`step-action-form-ref-${st.key}`} className="w-full h-8 rounded border border-slate-300 bg-white text-xs text-slate-800 px-2">
            <option value="">— Form pubblicato —</option>{(refs.forms || []).map((f) => <option key={f.id} value={f.id}>{f.name} · v{f.published_version}{f.mode === "mock" ? " · MOCK" : ""}</option>)}
          </select>
          <select value={action.template_id || ""} onChange={(e) => onChange({ ...action, type, template_id: e.target.value || null })} data-testid={`step-action-form-template-${st.key}`} className="w-full h-8 rounded border border-slate-300 bg-white text-xs text-slate-800 px-2">
            <option value="">Template di riserva (fuori finestra 24h) — nessuno</option>{(refs.templates || []).filter((t) => t.status === "approved").map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <p className="text-[11px] text-slate-500">Invia il Flow pubblicato una sola volta per contatto e step (nessun doppio invio). Fuori dalla finestra 24h serve un template approvato con pulsante Flow.</p>
        </div>
      )}
      {type === "pause" && (
        <div className="space-y-1" data-testid={`step-action-pause-${st.key}`}>
          <label className="flex items-center gap-2 text-xs text-slate-300">Durata pausa
            <Input type="number" min="1" max="365" value={action.days ?? 30} onChange={(e) => onChange({ ...action, type, days: Number(e.target.value) || 30 })}
                   data-testid={`step-action-pause-days-${st.key}`} className="h-7 w-20 bg-[#0A0E17] border-[#1F2937] text-xs" /> giorni
          </label>
          <p className="text-[11px] text-slate-500">Il contatto resta iscritto ma viene escluso da tutte le campagne fino alla scadenza, poi torna attivo da solo. Se scrive «Sì» o «Iscrivimi» la pausa termina subito.</p>
        </div>
      )}
      {type === "link" && (
        <div className="space-y-2" data-testid={`step-action-link-${st.key}`}>
          <Input value={action.ref || ""} onChange={(e) => onChange({ ...action, type, ref: e.target.value })} data-testid={`step-action-link-url-${st.key}`}
                 placeholder="https://www.ilmiosito.it/offerta?carta={{card_code}}" className="bg-[#0A0E17] border-[#1F2937] text-xs font-mono" />
          <Input value={action.text || ""} onChange={(e) => onChange({ ...action, type, text: e.target.value })} data-testid={`step-action-link-text-${st.key}`}
                 placeholder="Testo prima del link (facoltativo), es. Scopri le offerte riservate a te 👇" className="bg-[#0A0E17] border-[#1F2937] text-xs" />
          <label className="flex items-center justify-between text-xs text-slate-300">
            <span>Traccia i clic (link accorciato /api/r/…: vedi chi apre la pagina)</span>
            <Switch checked={!!action.track} onCheckedChange={(v) => onChange({ ...action, type, track: v })} data-testid={`step-action-link-track-${st.key}`} />
          </label>
          <p className="text-[11px] text-slate-500">Il link viene aggiunto in fondo al messaggio dello step. Indirizzo fisso oppure variabile con {"{{nome}}"}, {"{{telefono|phone}}"}, {"{{card_code}}"}, {"{{email}}"} o qualsiasi campo personalizzato: le variabili vengono sostituite con i dati del cliente che riceve il messaggio.</p>
        </div>
      )}
      {type === "sms" && (
        <div className="space-y-1">
          <Textarea value={action.ref || ""} onChange={(e) => setRef(e.target.value)} rows={2} data-testid={`step-action-sms-${st.key}`}
                    placeholder="Testo SMS, es. Ciao {{nome}}, la tua richiesta è stata ricevuta." className="bg-[#0A0E17] border-[#1F2937] text-xs" />
          <p className="text-[11px] text-slate-500">{(action.ref || "").length} caratteri · variabili {"{{nome}}"} e {"{{message}}"} ammesse · inviato anche a finestra WhatsApp chiusa.</p>
        </div>
      )}
      {type === "operator" && (
        <p className="text-[11px] text-[#A78BFA]" data-testid={`step-action-operator-${st.key}`}>Quando il cliente arriva a questo nodo l'Assistente IA smette di rispondere, la chat viene etichettata "Urgente" e l'operatore riceve la notifica. Si riattiva con "Segna come gestita" in Chat.</p>
      )}
      {type !== "none" && type !== "sms" && type !== "operator" && type !== "link" && type !== "pause" && (
        <Select value={action.ref || ""} onValueChange={setRef}>
          <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-8 w-full" data-testid={`step-action-ref-${st.key}`}>
            <SelectValue placeholder={items.length ? "Seleziona…" : "Nessun elemento disponibile"} />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
            {items.map((it) => <SelectItem key={it.id} value={it.id}>{labelOf(it)}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {type === "template" && action.ref && <TemplateParamsEditor tpl={items.find((it) => it.id === action.ref)} action={action} onChange={onChange} customFields={refs.customFields || []} stepKey={st.key} />}
      {type === "template" && <p className="text-[11px] text-slate-500">Invia il template approvato selezionato (oltre al messaggio dello step).</p>}
      {type === "webhook" && <p className="text-[11px] text-slate-500">Invia una chiamata POST al webhook scelto quando si raggiunge questo step.</p>}
      {type === "automation" && <p className="text-[11px] text-slate-500">Passa il cliente al percorso dell'automazione selezionata.</p>}
      {type === "cluster" && <p className="text-[11px] text-slate-500">Aggiunge il contatto al cluster scelto quando raggiunge questo step (le liste dinamiche basate sul cluster si aggiornano da sole).</p>}
      {type === "email" && <p className="text-[11px] text-slate-500">Invia il template email scelto al contatto (solo se ha un indirizzo email e il consenso email attivo).</p>}
      {type !== "none" && type !== "sms" && type !== "operator" && type !== "link" && type !== "pause" && items.length === 0 && <p className="text-[11px] text-amber-400">{type === "template" ? "Nessun template disponibile: crea o importa i template dalla sezione Template." : type === "automation" ? "Nessun'altra automazione disponibile: creane un'altra per poterla collegare." : type === "email" ? "Nessun template email: creane uno nella sezione Email transazionali." : type === "webhook" ? "Nessun webhook configurato in Integrazioni." : "Nessun cluster disponibile."}</p>}
    </div>
  );
}

const KB_STD_FIELDS = [["name", "Nome"], ["email", "Email"], ["store", "Punto vendita"], ["city", "Città"], ["card_code", "Codice carta"]];

function StepCaptureEditor({ st, steps, onChange }) {
  const cap = st.capture || null;
  const isCustom = cap && !KB_STD_FIELDS.some(([v]) => v === cap.field);
  return (
    <div className="bg-[#111827] border border-[#1F2937] rounded-lg p-2 space-y-2" data-testid={`step-capture-${st.key}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-500">Salva la risposta libera del cliente</span>
        <Switch checked={!!cap} onCheckedChange={(v) => onChange(v ? { field: "name", next_key: "" } : null)} data-testid={`step-capture-toggle-${st.key}`} />
      </div>
      {cap && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-400">nel campo</span>
          <Select value={isCustom ? "__custom__" : cap.field} onValueChange={(v) => onChange({ ...cap, field: v === "__custom__" ? "" : v })}>
            <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-8 w-36" data-testid={`step-capture-field-${st.key}`}><SelectValue placeholder="Campo" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
              {KB_STD_FIELDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              <SelectItem value="__custom__">Personalizzato…</SelectItem>
            </SelectContent>
          </Select>
          {isCustom && <Input value={cap.field || ""} onChange={(e) => onChange({ ...cap, field: e.target.value })} placeholder="nome campo" className="bg-[#0A0E17] border-[#1F2937] h-8 w-28" data-testid={`step-capture-custom-${st.key}`} />}
          <Select value={cap.mode || "free"} onValueChange={(v) => onChange({ ...cap, mode: v })}>
            <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-8 w-40" data-testid={`step-capture-mode-${st.key}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
              <SelectItem value="free">Risposta libera (testo)</SelectItem>
              <SelectItem value="single">Risposta unica (pulsanti)</SelectItem>
              <SelectItem value="multi">Più risposte (pulsanti)</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[11px] text-slate-400">poi</span>
          <Select value={cap.next_key || "end"} onValueChange={(v) => onChange({ ...cap, next_key: v === "end" ? "" : v })}>
            <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-8 w-40" data-testid={`step-capture-next-${st.key}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
              <SelectItem value="end">Fine percorso (Grazie!)</SelectItem>
              {steps.filter((s2) => s2.key !== st.key).map((s2) => <SelectItem key={s2.key} value={s2.key}>Vai a Step {steps.indexOf(s2) + 1}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="w-full text-[10px] text-slate-500">Qualsiasi testo scritto dal cliente (es. il suo nome) viene salvato nella scheda e il percorso prosegue.</p>
        </div>
      )}
    </div>
  );
}

function StepUpdatesEditor({ st, onChange }) {
  const updates = st.updates || [];
  const add = (kind) => onChange([...updates, kind === "tag" ? { kind: "tag", value: "" } : { kind: "field", field: "", value: "", custom: false }]);
  const set = (i, patch) => onChange(updates.map((u, idx) => (idx === i ? { ...u, ...patch } : u)));
  const del = (i) => onChange(updates.filter((_, idx) => idx !== i));

  return (
    <div className="bg-[#111827] border border-[#1F2937] rounded-lg p-2 space-y-2" data-testid={`step-updates-${st.key}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-500">Aggiornamenti anagrafica</span>
        <div className="flex gap-2">
          <button type="button" onClick={() => add("field")} data-testid={`step-add-field-${st.key}`} className="text-[11px] text-[#10B981] hover:underline">+ Campo</button>
          <button type="button" onClick={() => add("tag")} data-testid={`step-add-tag-${st.key}`} className="text-[11px] text-[#8B5CF6] hover:underline">+ Tag</button>
        </div>
      </div>
      {updates.map((u, i) => (
        <div key={i} className="flex items-center gap-2">
          {(u.kind || u.type) === "tag" ? (
            <>
              <Badge className="bg-[#8B5CF6]/15 text-[#8B5CF6] border-0 text-[10px] shrink-0">TAG</Badge>
              <Input value={u.value || ""} onChange={(e) => set(i, { value: e.target.value })} data-testid={`step-tag-value-${st.key}-${i}`}
                     placeholder="Nome tag (es. Interessato)" className="bg-[#0A0E17] border-[#1F2937] h-8 flex-1" />
            </>
          ) : (
            <>
              <Badge className="bg-[#10B981]/15 text-[#10B981] border-0 text-[10px] shrink-0">CAMPO</Badge>
              <Select value={u.custom ? "__custom__" : (u.field || "")}
                      onValueChange={(v) => v === "__custom__" ? set(i, { custom: true, field: "" }) : set(i, { custom: false, field: v })}>
                <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-8 w-36" data-testid={`step-field-sel-${st.key}-${i}`}><SelectValue placeholder="Campo" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
                  {KB_STD_FIELDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  <SelectItem value="__custom__">Personalizzato…</SelectItem>
                </SelectContent>
              </Select>
              {u.custom && (
                <Input value={u.field || ""} onChange={(e) => set(i, { field: e.target.value })} data-testid={`step-field-name-${st.key}-${i}`}
                       placeholder="nome campo" className="bg-[#0A0E17] border-[#1F2937] h-8 w-28" />
              )}
              <Input value={u.value || ""} onChange={(e) => set(i, { value: e.target.value })} data-testid={`step-field-value-${st.key}-${i}`}
                     placeholder="Valore" className="bg-[#0A0E17] border-[#1F2937] h-8 flex-1" />
            </>
          )}
          <button type="button" onClick={() => del(i)} className="text-slate-500 hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {updates.length > 0 && <p className="text-[10px] text-slate-500">Usa <span className="font-mono2">{"{{message}}"}</span> per salvare il testo scritto dal cliente.</p>}
    </div>
  );
}

function AutomationDialog({ existing, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cycleAck, setCycleAck] = useState(false);
  const [refs, setRefs] = useState({ templates: [], webhooks: [], automations: [], clusters: [], emailTemplates: [] });
  const [customVars, setCustomVars] = useState([]);

  useEffect(() => {
    if (!open) return;
    // allSettled: se una sola chiamata fallisce (es. webhook non disponibili) le altre liste restano utilizzabili
    Promise.allSettled([api.get("/templates"), api.get("/webhooks"), api.get("/automations"), api.get("/clusters"), api.get("/email-templates"), api.get("/contacts/custom-fields"), api.get("/forms/published")])
      .then(([t, w, a, cl, em, cf, fo]) => {
        const val = (r) => (r.status === "fulfilled" ? (Array.isArray(r.value.data) ? r.value.data : r.value.data?.items || []) : []);
        setRefs({
          forms: val(fo),
          templates: val(t),
          webhooks: val(w),
          automations: val(a).filter((x) => x.id !== existing?.id),
          clusters: val(cl),
          emailTemplates: val(em),
          customFields: val(cf),
        });
      });
    api.get("/contacts/custom-fields").then((r) => setCustomVars(r.data || [])).catch(() => setCustomVars([]));
  }, [open, existing]);

  useEffect(() => {
    if (open) {
      setCycleAck(false);
      setForm(existing
        ? { ...existing, steps: existing.steps?.length ? existing.steps : [{ key: "s1", message: "", options: [] }] }
        : { name: "", enabled: true, trigger_type: "keyword", trigger_value: "", priority: 0, steps: [{ key: "s1", message: "", options: [] }] });
    }
  }, [open, existing]);

  if (!form) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100" />
      </Dialog>
    );
  }

  const nextKey = () => {
    const nums = form.steps.map((s) => parseInt((s.key || "s0").replace("s", ""), 10) || 0);
    return "s" + (Math.max(0, ...nums) + 1);
  };
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { key: nextKey(), message: "", options: [] }] }));
  const addAskNameStep = () => setForm((f) => {
    const k = nextKey();
    const steps = [...f.steps, { key: k, message: "Piacere di conoscerti! Come ti chiami? 😊", options: [], capture: { field: "name", next_key: "" } }];
    return { ...f, steps };
  });
  const addBackToMenu = (key) => setStep(key, { options: [...(form.steps.find((s) => s.key === key).options || []), { label: "Torna al menu", next_key: form.steps[0].key, back_to_menu: true }] });
  const removeStep = (key) => setForm((f) => ({ ...f, steps: f.steps.filter((s) => s.key !== key) }));
  const setStep = (key, patch) => setForm((f) => ({ ...f, steps: f.steps.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));
  const addOption = (key) => setStep(key, { options: [...(form.steps.find((s) => s.key === key).options || []), { label: "", next_key: "" }] });
  const setOption = (key, idx, patch) => {
    const st = form.steps.find((s) => s.key === key);
    const opts = st.options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    setStep(key, { options: opts });
  };
  const removeOption = (key, idx) => {
    const st = form.steps.find((s) => s.key === key);
    setStep(key, { options: st.options.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci un nome"); return; }
    if (["keyword", "exact", "regex"].includes(form.trigger_type) && !form.trigger_value.trim()) {
      toast.error("Inserisci il valore del trigger"); return;
    }
    if (form.trigger_type === "date" && !form.trigger_value.trim()) {
      toast.error("Seleziona una data"); return;
    }
    if (!form.steps.length || !form.steps[0].message.trim()) { toast.error("Il primo step deve avere un messaggio"); return; }
    const verr = validateSteps(form.steps);
    if (verr) { toast.error(verr); return; }
    const cyc = findCycle(form.steps);
    if (cyc && !cycleAck) { toast.warning(cycleMessage(form.steps, cyc) + ". Clicca di nuovo per salvare comunque."); setCycleAck(true); return; }
    setSaving(true);
    try {
      const payload = { ...form, start_key: form.steps[0].key };
      let saved;
      if (existing) saved = (await api.put(`/automations/${existing.id}`, payload)).data;
      else saved = (await api.post("/automations", payload)).data;
      toast.success(existing ? "Automazione aggiornata" : "Automazione creata");
      setOpen(false);
      onDone(saved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const needsText = ["keyword", "exact", "regex"].includes(form.trigger_type);
  const needsHours = form.trigger_type === "non_working_hours";
  const textLabel = { keyword: "Parola chiave da cercare", exact: "Testo esatto", regex: "Espressione regolare (regex)" }[form.trigger_type];
  const textPlaceholder = { keyword: "Es. orari", exact: "Es. voglio prenotare", regex: "Es. ^(ciao|salve)" }[form.trigger_type];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Modifica automazione" : "Nuova automazione"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nome</Label>
            <Input data-testid="automation-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937]" placeholder="Es. Risponditore orari" /></div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Trigger</Label>
              <Select value={form.trigger_type} onValueChange={(v) => setForm({ ...form, trigger_type: v })}>
                <SelectTrigger className="bg-[#0A0E17] border-[#1F2937]" data-testid="automation-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
                  {TRIGGER_GROUPS.map((g) => (
                    <SelectGroup key={g.group}>
                      <SelectLabel className="text-slate-500 text-xs">{g.group}</SelectLabel>
                      {g.items.map((k) => <SelectItem key={k} value={k}>{TRIGGERS[k].label}</SelectItem>)}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Priorità</Label>
              <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "0", 10) })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
          </div>

          {needsText && (
            <div className="space-y-2"><Label>{textLabel}</Label>
              <Input data-testid="automation-trigger-value" value={form.trigger_value} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937] font-mono2" placeholder={textPlaceholder} />
              {form.trigger_type === "regex" && <p className="text-xs text-slate-500">L'automazione parte se il messaggio corrisponde all'espressione (case-insensitive).</p>}
            </div>
          )}
          {needsHours && (
            <div className="space-y-2"><Label>Orario lavorativo (fuori da questo = attiva)</Label>
              <Input data-testid="automation-trigger-hours" value={form.trigger_value || "09:00-18:00"} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937] font-mono2" placeholder="09:00-18:00" />
              <p className="text-xs text-slate-500">Risponde automaticamente ai messaggi ricevuti fuori da questa fascia oraria (UTC).</p>
            </div>
          )}
          {form.trigger_type === "welcome" && <p className="text-xs text-slate-500">Si attiva al primo messaggio di un nuovo cliente.</p>}
          {form.trigger_type === "fallback" && <p className="text-xs text-slate-500">Si attiva per qualsiasi messaggio non gestito da altre automazioni.</p>}
          {form.trigger_type === "marketing_optin" && <p className="text-xs text-slate-500">Si attiva quando un cliente dà il consenso marketing (es. risponde ISCRIVIMI/SI). Invia un messaggio di benvenuto.</p>}
          {form.trigger_type === "link_click" && <p className="text-xs text-slate-500" data-testid="link-click-hint">Si attiva al primo clic di un contatto su un pulsante Link di un template: collegala dal Template → "Al clic avvia automazione" (tracciamento per contatto). Il messaggio parte solo se la finestra 24h è aperta.</p>}
          {form.trigger_type === "date" && (
            <div className="space-y-2"><Label>Data di invio</Label>
              <Input type="date" data-testid="automation-trigger-date" value={(form.trigger_value || "").slice(0, 10)} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" />
              <p className="text-xs text-slate-500">Il messaggio parte automaticamente agli iscritti nella data scelta (promemoria, scadenze).</p>
            </div>
          )}
          {form.trigger_type === "holiday" && <p className="text-xs text-slate-500">Si attiva nei giorni festivi italiani (es. Natale, Capodanno, Ferragosto) per inviare auguri agli iscritti.</p>}
          {form.trigger_type === "webhook" && <p className="text-xs text-slate-500">Si attiva da una chiamata esterna all'URL webhook dell'automazione.</p>}

          <div className="border-t border-[#1F2937] pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-300">Percorso (step)</p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={addAskNameStep} data-testid="add-ask-name-step-button"
                        className="bg-[#1E293B] border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#334155] h-8">
                  <UserRound className="h-3.5 w-3.5 mr-1" /> Chiedi il nome
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addStep} data-testid="add-step-button"
                        className="bg-[#1E293B] border-[#1F2937] text-slate-200 h-8">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi step
                </Button>
              </div>
            </div>
            {form.steps.map((st, si) => (
              <div key={st.key} data-testid={`step-${st.key}`} className="bg-[#0A0E17] border border-[#1F2937] rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge className="bg-[#1E293B] text-slate-300 border-0 text-xs">Step {si + 1}{si === 0 ? " (inizio)" : ""}</Badge>
                  {si > 0 && <button type="button" onClick={() => removeStep(st.key)} className="text-slate-500 hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
                <Textarea rows={2} data-testid={`step-message-${st.key}`} value={st.message} onChange={(e) => setStep(st.key, { message: e.target.value })}
                          className="bg-[#111827] border-[#1F2937] resize-none" placeholder="Messaggio di risposta (usa {{nome}} per il nome)" />
                <div className="flex flex-wrap gap-1.5" data-testid={`step-vars-${st.key}`}>
                  {["nome", "email", "store", "city", "card_code", ...customVars].map((v) => (
                    <button key={v} type="button"
                            onClick={() => setStep(st.key, { message: (st.message ? st.message + " " : "") + `{{${v}}}` })}
                            className={`px-2 py-0.5 rounded-md text-[11px] font-mono2 ${customVars.includes(v) ? "bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25" : "bg-[#8B5CF6]/15 text-[#8B5CF6] hover:bg-[#8B5CF6]/25"}`}>
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500">Usa <span className="font-mono2">{"{{campo|valore di riserva}}"}</span> per un testo predefinito se il dato manca.</p>
                <StepActionEditor st={st} refs={refs} onChange={(action) => setStep(st.key, { action })} />
                <div className="bg-[#111827] border border-[#1F2937] rounded-lg p-2 flex items-center gap-2" data-testid={`step-fallback-${st.key}`} title="Il testo libero è consentito da Meta solo entro 24 ore dall'ultimo messaggio del cliente. Fuori finestra viene inviato SOLO il template scelto qui; senza template l'invio resta bloccato.">
                  <span className="text-[11px] text-amber-400 shrink-0">Se finestra 24h chiusa →</span>
                  <select value={st.fallback_template_id || ""} data-testid={`step-fallback-select-${st.key}`} onChange={(e) => setStep(st.key, { fallback_template_id: e.target.value || null })}
                          className="flex-1 min-w-0 h-8 rounded border border-[#1F2937] bg-[#0A0E17] text-xs text-slate-100 px-2">
                    <option value="">nessun template (invio bloccato)</option>
                    {(refs.templates || []).filter((t) => t.status === "approved" && (t.meta_name || t.meta_template_name)).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <StepUpdatesEditor st={st} onChange={(updates) => setStep(st.key, { updates })} />
                <StepCaptureEditor st={st} steps={form.steps} onChange={(capture) => setStep(st.key, { capture })} />
                {(st.options || []).length > 0 && <StepListUiEditor st={st} onChange={(p) => setStep(st.key, p)} />}
                <div className="space-y-2">
                  {(st.options || []).map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      {o.back_to_menu && <Badge className="bg-[#3B82F6]/15 text-[#3B82F6] border-0 text-[10px] shrink-0" data-testid={`option-back-badge-${st.key}-${oi}`}>menu</Badge>}
                      <Input value={o.label} onChange={(e) => setOption(st.key, oi, { label: e.target.value })}
                             data-testid={`option-label-${st.key}-${oi}`}
                             className={`bg-[#111827] h-8 flex-1 ${(o.label || "").trim() ? "border-[#1F2937]" : "border-[#EF4444]/70"}`} placeholder={`Opzione ${oi + 1} (obbligatoria)`} />
                      <Select value={o.next_key || "end"} onValueChange={(v) => setOption(st.key, oi, { next_key: v === "end" ? "" : v })}>
                        <SelectTrigger className="bg-[#111827] border-[#1F2937] h-8 w-40" data-testid={`option-next-${st.key}-${oi}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100 z-[90]">
                          <SelectItem value="end">Fine percorso</SelectItem>
                          {form.steps.map((s2, i2) => <SelectItem key={s2.key} value={s2.key}>Vai a Step {i2 + 1}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button type="button" onClick={() => removeOption(st.key, oi)} className="text-slate-500 hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <div className="flex gap-3">
                    <button type="button" onClick={() => addOption(st.key)} data-testid={`add-option-${st.key}`}
                            className="text-xs text-[#10B981] hover:underline">+ Aggiungi opzione</button>
                    {si > 0 && !(st.options || []).some((o) => o.back_to_menu) && (
                      <button type="button" onClick={() => addBackToMenu(st.key)} data-testid={`add-back-to-menu-${st.key}`}
                              className="text-xs text-[#3B82F6] hover:underline">+ Torna al menu</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <div className="flex-1 min-w-0"><CycleWarning steps={form.steps} /></div>
          <Button onClick={save} disabled={saving} data-testid="save-automation-button"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (existing ? "Salva modifiche" : "Crea automazione")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesDialog({ onInstalled }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => { if (open) api.get("/automations/templates").then((r) => setItems(r.data || [])).catch(() => setItems([])); }, [open]);
  const install = async (t) => {
    setBusy(t.id);
    try {
      const { data } = await api.post(`/automations/templates/${t.id}`);
      toast.success(`«${data.name}» creata (disattivata): personalizzala e attivala`);
      setOpen(false);
      onInstalled?.(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Installazione non riuscita"); } finally { setBusy(""); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="automation-templates-button" className="bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg">
          <Sparkles className="h-4 w-4 mr-2 text-[#5BC65F]" /> Modelli pronti
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl" data-testid="automation-templates-dialog">
        <DialogHeader><DialogTitle className="text-slate-900 text-lg">Automazioni pronte all'uso</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Un clic crea l'automazione (disattivata) nella cartella «Modelli»: la apri nel costruttore, la adatti e la attivi.</p>
        <div className="grid sm:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto pr-1">
          {items.map((t) => { const tr = TRIGGERS[t.trigger_type] || TRIGGERS.keyword; return (
            <div key={t.id} className="rounded-xl border border-slate-200 p-3 flex flex-col gap-2 hover:shadow-sm" data-testid={`template-card-${t.id}`}>
              <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
              <p className="text-xs text-slate-500 flex-1">{t.desc}</p>
              <div className="flex items-center justify-between gap-2">
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${tr.cls}`}>{tr.label}{t.trigger_value ? `: ${t.trigger_value}` : ""} · {t.steps} nodi</span>
                <Button size="sm" onClick={() => install(t)} disabled={!!busy} data-testid={`template-install-${t.id}`} className="h-7 text-xs bg-[#5BC65F] hover:bg-[#45B049] text-white">
                  {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Usa modello"}
                </Button>
              </div>
            </div>); })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SimulateDialog() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isFirst, setIsFirst] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/automations/simulate", { message, is_first: isFirst });
      setResult(data);
    } catch {
      toast.error("Errore nella simulazione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="simulate-automation-button"
                className="bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg">
          <Play className="h-4 w-4 mr-2" /> Simula
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Simula un messaggio in arrivo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="simulate-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
                 className="bg-[#0A0E17] border-[#1F2937]" placeholder="Scrivi il messaggio del cliente..." />
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <Switch checked={isFirst} onCheckedChange={setIsFirst} data-testid="simulate-first-switch" /> È il primo messaggio del cliente
          </label>
          <Button onClick={run} disabled={busy} data-testid="simulate-run-button" className="w-full bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Esegui simulazione"}
          </Button>
          {result && (
            <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl p-3" data-testid="simulate-result">
              {result.matched
                ? <><p className="text-xs text-[#10B981] mb-1">Attivata: {result.automation}</p><p className="text-sm text-slate-200 whitespace-pre-wrap">{result.reply}</p></>
                : <p className="text-sm text-slate-500">Nessuna automazione corrisponde → risponderebbe l'Assistente IA.</p>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
